import { useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import type { BrokerLoginDto } from '@apna/shared'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'

/**
 * Replace the key, the secret, or both, on an existing credential.
 *
 * `PATCH /logins/:id/secrets` existed with nothing calling it, which meant
 * rotating a key in the UI required Delete then Add. For a DEALER credential
 * that is actively dangerous: deleting it detaches every account it serves,
 * and between the delete and the re-add those accounts cannot trade. Rotation
 * in place keeps the attachments.
 *
 * Either field may be left blank to keep the current value, because brokers
 * do not always reissue both halves. Nothing is pre-filled — the stored values
 * are ciphertext and the API never returns them.
 */
export function RotateSecretsDialog({
  login,
  attachedCount,
  onClose,
  onDone,
}: {
  login: BrokerLoginDto
  attachedCount: number
  onClose: () => void
  onDone: () => void
}) {
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nothingEntered = !apiKey.trim() && !apiSecret.trim()

  const submit = async () => {
    if (nothingEntered) {
      setError('Enter a new key, a new secret, or both.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.patch(`/broker/logins/${login.id}/secrets`, {
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        ...(apiSecret.trim() ? { apiSecret: apiSecret.trim() } : {}),
      })
      onDone()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Replace the secrets for &ldquo;{login.label}&rdquo;</DialogTitle>
          <DialogDescription>
            {login.providerKey} · {login.role === 'INTERACTIVE' ? 'Orders' : 'Market data'}. Leave a
            field blank to keep what is stored.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="rotate-key">New API key</Label>
            <Input
              id="rotate-key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="leave blank to keep the current key"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rotate-secret">New API secret</Label>
            <Input
              id="rotate-secret"
              // type=password so a secret is not left on screen during a
              // screen-share, which is how these get leaked in practice.
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder="leave blank to keep the current secret"
              autoComplete="new-password"
              spellCheck={false}
            />
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-[11px] leading-relaxed text-warning">
              The stored session token was issued against the old secret, so it is discarded and
              the next call logs in again.
              {attachedCount > 0 && (
                <>
                  {' '}
                  <span className="font-medium">
                    {attachedCount} account{attachedCount > 1 ? 's' : ''}
                  </span>{' '}
                  use this credential and will pick up the new one — they stay attached.
                </>
              )}
            </p>
          </div>

          {error && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || nothingEntered}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {busy ? 'Replacing…' : 'Replace secrets'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
